import { yupResolver } from '@hookform/resolvers/yup';
import { FormControl, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import DatePicker from 'components/CustomInputs/DatePicker';
import IntegerField from 'components/CustomInputs/IntegerField';
import SelectWithErrorField from 'components/CustomInputs/SelectWithErrorField';
import TimePicker from 'components/CustomInputs/TimePicker';
import WeightInputField from 'components/CustomInputs/WeightInputField';
import { isWeightAgainstAvegerageWeight } from 'services/math';
import { AppContext } from 'context';
import { useBuyersFromMaterialWithClientContract, useClientContracts, useMaterials } from 'hooks';
import { ExpeditionForm, Material } from 'types';
import { useLatestClosureDate } from 'utils/closureHooks';
import { isMaterialConditionnedInBalls } from 'utils/formValidation';
import client from 'services/client';
import { formatDateAsQueryParam, getISOStringWithTimeZone } from 'utils/date';
import { transformId } from 'services/apiPlatformTransformer';
import { expeditionFormSchema } from './Step1.yup';
import { FormStepComponentProps } from '../MultiStepForm';
import { notify } from '../../Notifier';
import { DEFAULT_ITEMS_PER_SELECT } from '../../../tableFilterContext';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexFlow: 'column',
    alignItems: 'center',
  },
  fieldLine: {
    marginTop: 8,

    '& > div': {
      minWidth: '892px',
    },

    '& span': {
      fontSize: 18,
      marginBottom: 5,
    },
  },
  sharedLine: {
    display: 'flex',
    justifyContent: 'space-between',

    '& > div:first-child': {
      width: '356px',
    },
    '& > div:last-child': {
      width: '510px',
    },
  },
  sharedSubline: {
    display: 'flex',
    justifyContent: 'space-between',

    '& > div': {
      width: '166px',
    },
  },
  tripleSubline: {
    display: 'flex',
    justifyContent: 'space-between',

    '& > div': {
      width: '154px',
    },
  },
});

const Step1: React.FC<FormStepComponentProps<ExpeditionForm>> = ({
  onSubmit,
  formName,
  previousStepData,
  setDuplicateModalOpen,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('translation', { keyPrefix: 'expeditionCreateForm' });
  const context = useContext(AppContext);
  const sortingCenter = context.getSortingCenter?.();
  const [expeditionDate, setExpeditionDate] = useState<string | null>(null);

  const materialChoices = useMaterials({
    itemsPerPage: String(DEFAULT_ITEMS_PER_SELECT),
    ...(expeditionDate && {
      provisionalOutboundStock: String(!!expeditionDate),
      expeditionDate: String(expeditionDate),
    }),
  });

  const [closureDate, twoMonthAfterClosureDate] = useLatestClosureDate(sortingCenter);
  const schema = useMemo(
    () => expeditionFormSchema(t, materialChoices, closureDate, twoMonthAfterClosureDate),
    [t, materialChoices, closureDate, twoMonthAfterClosureDate],
  );
  const { control, handleSubmit, watch, setValue, trigger, getValues, clearErrors } = useForm<ExpeditionForm>({
    mode: 'onBlur',
    defaultValues: previousStepData,
    resolver: yupResolver(schema, { abortEarly: false }),
  });
  const watchMaterial = watch('material', previousStepData.material);
  const [selectedMaterial, setSelectedMaterial] = useState<Material | undefined>();
  const watchExpeditionDate = watch('expeditionDate', previousStepData.expeditionDate);

  const [clientContracts] = useClientContracts(
    {
      material: selectedMaterial ? selectedMaterial['@id'] : '',
      isActive: '1',
    },
    1,
    1000,
    !!selectedMaterial,
  );

  const buyerChoices = useBuyersFromMaterialWithClientContract(watchMaterial, clientContracts);

  const checkWeightAgainstAverageWeight = () => {
    if (
      Number(getValues('conditioningQuantity')) > 0 &&
      Number(getValues('weight')) > 0 &&
      !isWeightAgainstAvegerageWeight(
        Number(getValues('conditioningQuantity')),
        Number(getValues('weight')) * 1000,
        Number(selectedMaterial?.averageWeight),
        15,
      )
    ) {
      notify(
        {
          message: t('create.warning.weight', {
            weight: (Number(getValues('weight')) / Number(getValues('conditioningQuantity')))
              .toFixed(3)
              .replace('.', ','),
            averageWeight: (Number(selectedMaterial?.averageWeight) / 1000).toFixed(3).replace('.', ','),
          }),
          severity: 'warning',
          width: 'small',
        },
        false,
      );
    } else {
      notify(null);
    }
  };

  useEffect(() => {
    if (!!materialChoices) {
      const newlySelectedMaterial = materialChoices.find((el: Material) => watchMaterial === el['@id']);
      setSelectedMaterial(newlySelectedMaterial);
    }
  }, [materialChoices, watchMaterial]);

  useEffect(() => {
    if (watchExpeditionDate && !isNaN(watchExpeditionDate.getTime())) {
      setExpeditionDate(formatDateAsQueryParam(watchExpeditionDate));
    } else {
      setExpeditionDate(null);
    }
  }, [watchExpeditionDate]);

  const handleStep1Submit = async (data: ExpeditionForm) => {
    if (!sortingCenter || !sortingCenter['@id']) {
      return;
    }

    const expeditionDate = getValues('expeditionDate');
    const expeditionTime = getValues('expeditionTime');

    const params = new URLSearchParams({
      buyer: data.buyer,
      material: data.material,
    });

    if (expeditionDate instanceof Date) {
      let expeditionDateTime = new Date(expeditionDate);

      if (expeditionTime instanceof Date) {
        expeditionDateTime.setHours(expeditionTime.getHours(), expeditionTime.getMinutes());
      } else {
        expeditionDateTime.setHours(0, 0, 0, 0);
      }

      params.append('expeditionDate', getISOStringWithTimeZone(expeditionDateTime));
    }

    try {
      const response = await client.get(
        `/sorting_centers/${transformId(sortingCenter['@id'])}/expeditions?${params.toString()}`,
      );

      if (response.length > 0) {
        if (setDuplicateModalOpen) {
          setDuplicateModalOpen(true);
        }
        return;
      } else {
        onSubmit(data);
      }
    } catch (error) {
      notify({
        message: t('expeditionCreate.creation.error'),
        severity: 'error',
      });
    }
  };

  return (
    <form id={formName} onSubmit={handleSubmit(handleStep1Submit)} className={classes.form}>
      <div className={classes.fieldLine}>
        <div className={classes.sharedLine}>
          <Controller
            name="material"
            control={control}
            render={({ field: { ref, onChange, onBlur, ...inputProps }, fieldState }) => (
              <SelectWithErrorField
                id="material"
                choices={materialChoices}
                choiceDisplayKey="shortName"
                selectLabel={t('material')}
                error={fieldState.invalid}
                errorMessage={fieldState.error?.message}
                inputRef={ref}
                onChange={e => {
                  onChange(e);
                  setValue('buyer', '');
                  setValue('conditioningQuantity', '');
                  setValue('weight', '');
                  clearErrors('conditioningQuantity');
                }}
                InputLabelProps={{ required: true }}
                {...inputProps}
              />
            )}
          />
          <Controller
            name="buyer"
            control={control}
            render={({ field: { ref, ...fieldProps }, fieldState }) => (
              <SelectWithErrorField
                id="buyer"
                choices={buyerChoices}
                choiceDisplayKey="name"
                selectLabel={t('buyer')}
                disabled={'' === watchMaterial}
                error={fieldState.invalid}
                errorMessage={fieldState.error?.message}
                inputRef={ref}
                InputLabelProps={{ required: true }}
                {...fieldProps}
              />
            )}
          />
        </div>
      </div>
      <div className={classes.fieldLine}>
        <div className={classes.sharedLine}>
          <div className={classes.sharedSubline}>
            <Controller
              name="expeditionDate"
              control={control}
              render={({ field: { ref, ...fieldProps }, fieldState }) => (
                <FormControl variant="outlined">
                  <DatePicker
                    id="expeditionDate"
                    label={t('date')}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    inputRef={ref}
                    InputLabelProps={{ required: true }}
                    onAccept={() => {
                      trigger('expeditionDate');
                    }}
                    {...fieldProps}
                  />
                </FormControl>
              )}
            />
            <Controller
              name="expeditionTime"
              control={control}
              render={({ field: { ref, ...fieldProps }, fieldState }) => (
                <FormControl variant="outlined">
                  <TimePicker
                    id="expeditionTime"
                    label={t('time')}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    inputRef={ref}
                    InputLabelProps={{ required: true }}
                    {...fieldProps}
                  />
                </FormControl>
              )}
            />
          </div>
          <div className={classes.tripleSubline}>
            <Controller
              name="conditioningQuantity"
              control={control}
              render={({ field: { ref, ...fieldProps }, fieldState }) => (
                <FormControl>
                  <IntegerField
                    id="conditioningQuantity"
                    label={t('conditioningQuantity')}
                    error={fieldState.invalid}
                    helperText={isMaterialConditionnedInBalls(selectedMaterial) && fieldState.error?.message}
                    disabled={!isMaterialConditionnedInBalls(selectedMaterial)}
                    inputRef={ref}
                    InputLabelProps={{ required: true }}
                    {...fieldProps}
                    onBlur={() => {
                      trigger(['conditioningQuantity', 'expeditionClientPortions']);
                      checkWeightAgainstAverageWeight();
                    }}
                  />
                </FormControl>
              )}
            />
            <FormControl>
              <Controller
                name="weight"
                control={control}
                render={({ field: { ref, ...fieldProps }, fieldState }) => (
                  <WeightInputField
                    id="weight"
                    label={t('weight')}
                    variant="outlined"
                    inputRef={ref}
                    error={fieldState.invalid}
                    helperText={fieldState.error?.message}
                    disabled={!selectedMaterial}
                    InputLabelProps={{ required: true }}
                    {...fieldProps}
                    onBlur={() => {
                      trigger(['weight', 'expeditionClientPortions']);
                      checkWeightAgainstAverageWeight();
                    }}
                  />
                )}
              />
            </FormControl>
            <FormControl>
              <Controller
                name="truckIdentifier"
                control={control}
                render={({ field: { ref, onBlur, ...fieldProps }, fieldState }) => (
                  <TextField
                    id="truckIdentifier"
                    label={t('truckIdentifier')}
                    variant="outlined"
                    inputRef={ref}
                    {...fieldProps}
                    helperText={fieldState.error?.message}
                    error={fieldState.invalid}
                    inputProps={{ maxLength: 100 }}
                  />
                )}
              />
            </FormControl>
          </div>
        </div>
      </div>
    </form>
  );
};

export default Step1;
